import { BusinessException, EntityModificationPack, Profile, User, UserApplication } from '@wizeoo/akm-message-lib';
import { StaticFeatureEnum } from '../../manager/permission/permissions.types';
import { FEATURE_PERIMETER } from '../../model/enums';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { BusinessUnitVO } from '../../vo/core/business-unit.vo';
import { ProfileVO } from '../../vo/user/profile.vo';
import { RoleVO } from '../../vo/user/role.vo';
import { UserApplicationVO } from '../../vo/user/user-app.vo';
import { UserVO } from '../../vo/user/user.vo';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';
import { WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';
import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { cloneDeep } from 'lodash-es';

export interface UserApplicationUpdate {
  key: string, // la clé unique pour cette APP
  operation: string, // CUD
  sourceApp: UserApplicationVO, // l'origine, inaltéré
  targetApp: UserApplicationVO, // l'app modifiée  
}

export interface UserUpdate {
  sourceUser: UserVO, // le user d'origine, inaltéré
  targetUser: UserVO, // le user modifié
  appUpdates: Map<string, UserApplicationUpdate>, // les modifs sur les userApp, key = UserApplicationUpdate.key
}


/** gestion du user, des profiles, des rôles... */
export class UserEngine {

  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }
  
  /** le dernier user édité */
  public currentEditedUser: UserVO = null;

  /**
   * Retourne la MEME instance de user que dans la liste du modèle.
   * @param source : UserVO cherché
   */
  public getCurrentInstance(source: UserVO): UserVO {
    let result: UserVO = null;

    // recherche d'une entrée de contexte utilisateur (key = login user)
    if (!source) {
      // seulement si recherche vide
      const contextUser: string = this._model.userContextService.getTab(StaticFeatureEnum.USER)?.selectedItemId;
      if (contextUser) {
        source = new UserVO(this.model);
        source.mail = contextUser;
      }
    }

    if (source && this._model.usersMap) {
      result = this._model.usersMap.get(source.mail);
    }
    return result;
  }

  /**
   * Création d'un user
   * @return UserVO Le user créé
   */
  public createUser(): UserVO {
    const result = new UserVO(this.model);
    result.domain = ViewData.wildcardDomain;
    result.businessUnitCode = ViewData.wildcardDomain;
    result.mail = null;
    result.firstname = null;
    result.lastname = null;
    result.active = false;
    return result;
  }

  /**
   * Clone de user
   * @param user Le user à cloner
   * @return UserVO Le clone du user
   */
  public clone(user: UserVO): UserVO {
    const clone = new UserVO(this.model, cloneDeep(user.source));
    return clone;
  }

  /**
   * Vérification que l'object user est valid (attribut login, domain et businessUnitCode)
   * TODO compléter
   * @param user Le user testé
   * @return boolean true s'il est valide, false sinon
   */
  public isValid(user: UserVO): boolean {
    if (!user) {
      return false;
    }

    if (!user.mail) {
      return false;
    }

    if (user.domain === undefined || user.domain === null) {
      return false;
    }

    if (user.businessUnitCode === undefined || user.businessUnitCode === null) {
      return false;
    }

    return true;
  }

  /** alimentation de la liste des userVO */
  public feed(users: User[]): void {
      

    if(!this._model.usersMap) {
      this._model.usersMap = new Map();
    }
    
    // convertion de la liste de retour en liste de UserVO
    users.forEach((us: User) => {
      const usvo: UserVO = new UserVO(this.model, us);
      this._model.usersMap.set(usvo.mail, usvo);
    });
  }

  /**
   * recherche d'un user par son ID (retour de modif sur appVO)
   * @param id 
   * @returns le UserVO de la map du modèle
   */
  public getUserById(id: number): UserVO {
    let byIdMap: Map<number, UserVO> = new Map([...this._model.usersMap.values()].map(item => [item.id, item]));
    return byIdMap.get(id) || null;
  }



  /** clé d'identification unique d'une fonctionalité */
  public key(src: UserApplicationVO): string {
    if (!src) {
      return '';
    }
    return [src.domain, src.businessUnitCode, src.visionCode, src.perimeter].join(ViewData.KEY_SEPARATOR);
  }

  /**
   * accesseur sur un userApp par périmètre
   * @param user: userVO cible
   * @param domain 
   * @param bu 
   * @param visionCode 
   * @param perimeter 
   * @returns 
   */
  public getUserAppFromMap(user: UserVO, domain: string, bu: string, visionCode: string, perimeter: string): UserApplicationVO {
    let result: UserApplicationVO = null;
    let userAppsByPerimeter : Map<string, Map<string, Map<string, Map<string, UserApplicationVO>>>> = user.userAppsByPerimeter;
    result = userAppsByPerimeter?.get(domain)?.get(bu)?.get(visionCode)?.get(perimeter);
    
    if(!!result) {
      return result;
    }
    return null;
  }

  /**
   * ajout d'un userApp sur le user
   * @param user 
   * @param userApp 
   */
  public addAppOnUser(user: UserVO, userApp: UserApplicationVO): void {
    user.userApplicationsVO.push(userApp);
    user.userApplications.push(userApp.source);
    this.feedMapByPerimeter(user, user.userAppsByPerimeter, [userApp]); // ajout à la map par périmètre
  }

  /**
   * Mise à jour du user_app en paramètres. on remplate la source du VO et on met à jour la liste des user apps
   * @param app
   */
  public updateAppOnUser(user: UserVO, app: UserApplication) {
    const existUserApp: UserApplicationVO = this.getUserApp(user, app.domain, app.businessUnitCode, app.visionCode, app.perimeter);
    if(!existUserApp) {
      // console.log('update user app not exist', app);
    } else {
      existUserApp.source = app;
      const srcAppMap: Map<string, UserApplication> = new Map(user.userApplications.map(item => [this.generateKey(item), item]));
      const exist = srcAppMap.get(this.generateKey(app));
      user.userApplications.splice(user.userApplications.indexOf(exist), 1, app);

      // console.log('updateAppOnUser', app.roles.length, existUserApp.rolesVO.length);
    }
  }

  private generateKey(src: UserApplication): string {
    let result: string = [src.domain, src.businessUnitCode, src.visionCode, src.perimeter].join(ViewData.KEY_SEPARATOR);
    return result;
  }


  /**
   * ajout d'un userApp dans la map par périmètre sur le user
   * @param user 
   * @param currentMap la map par périmètre existante
   * @param userApp 
   */
   public feedMapByPerimeter(user: UserVO, currentMap: Map<string, Map<string, Map<string, Map<string, UserApplicationVO>>>> , userApp: UserApplicationVO[]): void {

    if(!user) {
      return;
    }

    userApp.forEach((uavo: UserApplicationVO) => {
      // construction de la map
      if(!currentMap) {
        currentMap = new Map();
      }
      if(!currentMap.has(uavo.domain)) {
        currentMap.set(uavo.domain, new Map());
      }
      if(!currentMap.get(uavo.domain).has(uavo.businessUnitCode)) {
        currentMap.get(uavo.domain).set(uavo.businessUnitCode, new Map());
      }
      if(!currentMap.get(uavo.domain).get(uavo.businessUnitCode).has(uavo.visionCode)) {
        currentMap.get(uavo.domain).get(uavo.businessUnitCode).set(uavo.visionCode, new Map());
      }

      currentMap.get(uavo.domain).get(uavo.businessUnitCode).get(uavo.visionCode).set(uavo.perimeter, uavo);
      
    });

    user.userAppsByPerimeter = currentMap;
  }

  /** 
   * retourne l'instance véritable sur le userVO (avant Merge)
   * 
   * @param user le user cible
   * @param app l'app de l'arbre des Apps
   * @param createIfNotExist Indique qu'il faut créer le user app s'il n'existe pas
   * @param returnFake ou renvoyer un fake
   * @returns la valeur de l'app portée par le user
   */
  public getUserAppFromApp(user: UserVO, app: UserApplicationVO, createIfNotExist: boolean = false, returnFake: boolean = false): UserApplicationVO {
    return this.getUserApp(user, app.domain, app.businessUnitCode, app.visionCode, app.perimeter, createIfNotExist, returnFake);
  }

  /**
   * retourne les userApp selon le niveau DOM / BU / vision
   * @param user: userVO cible
   * @param domain Le domain à filtrer
   * @param bu La bu à filtrer
   * @param perimeter le périmètre cible
   * @param createIfNotExist Indique qu'il faut créer le user app s'il n'existe pas
   * @param returnFake ou renvoyer un fake
   */
   public getUserApp(user: UserVO, domain: string, bu: string = '*', visionCode: string = '*', perimeter: string = null, createIfNotExist: boolean = false, returnFake: boolean = false): UserApplicationVO {
    if(!user) { // pas de user ? rien
      return null;
    }

    if (!bu) {
      bu = ViewData.wildcardDomain;
    }

    if (!visionCode) {
      visionCode = ViewData.wildcardDomain;
    }

    // résolution du périmètre de l'app
    if (!perimeter) {
      if (bu === ViewData.wildcardDomain) {
        perimeter = FEATURE_PERIMETER.DOM;
      } else if (visionCode === ViewData.wildcardDomain) {
        perimeter = FEATURE_PERIMETER.BU;
      } else {
        perimeter = FEATURE_PERIMETER.APP_CONF; // TODO ou APP ?
      }
    }

    let userApp = this.getUserAppFromMap(user, domain, bu, visionCode, perimeter);
    
    if (!userApp && !returnFake && !createIfNotExist) {
      return null;
    }

    if (!userApp) {
      userApp = new UserApplicationVO(this.model);
      userApp.businessUnitCode = bu;
      userApp.domain = domain;
      userApp.visionCode = visionCode;
      userApp.rolesVO = [];
      userApp.perimeter = perimeter;
      // locké par défaut, c'est à l'utilisateur de délocker
      userApp.doNone = false;
      // On doit censurer les userApplications sur le owner pour éviter les références circulaires
      userApp.ownerId = user.id;

      if (createIfNotExist) {
        this.addAppOnUser(user, userApp); // ajout aux collections du user
      }

      // return returnFake ? userApp : null;
    }

    // on s'assure que le owner id soit setté
    userApp.ownerId = user.id;
    return userApp;
  }

  /**
   * Vérifie si l'utilisateur n'est pas en doNone sur le userApp en paramètre ou à un niveau plus élevé
   * @param userApp le scope en cours
   */
  public isGlobalDoNone(user: UserVO, userApp: UserApplicationVO): boolean {
    // résolution ascendante tant qu'on un parentCode pour la bu
    if (userApp.businessUnitCode !== ViewData.wildcardDomain) {
      let buvo: BusinessUnitVO = this._model.businessUnitEngine.getByBusinessUnitCode(userApp.domain, userApp.businessUnitCode);

      while (!!buvo && !!buvo.parentCode) {
        const parentApp = this.getUserApp(user, userApp.domain, buvo.parentCode);

        if (!!parentApp && parentApp.doNone) {
          return true;
        }

        buvo = this._model.businessUnitEngine.getByBusinessUnitCode(userApp.domain, buvo.parentCode);
      }
    }

    // on finit par le domaine
    const domainApp = this.getUserApp(user, userApp.domain);

    if (!!domainApp && domainApp.doNone) {
      return true;
    }
  }

  /**
   * Vérifie si le user a un user app sur un niveau supérieur à celui en paramètre
   */
  public hasHigherUserApp(user: UserVO, userApp: UserApplicationVO) {
    // résolution ascendante tant qu'on un parentCode pour la bu
    if (userApp.businessUnitCode !== ViewData.wildcardDomain) {
      let buvo: BusinessUnitVO = this._model.businessUnitEngine.getByBusinessUnitCode(userApp.domain, userApp.businessUnitCode);

      while (!!buvo && !!buvo.parentCode) {
        const parentApp = this.getUserApp(user, userApp.domain, buvo.parentCode);

        if (!!parentApp) {
          return true;
        }

        buvo = this._model.businessUnitEngine.getByBusinessUnitCode(userApp.domain, buvo.parentCode);
      }
    }

    // on finit par le domaine
    const domainApp = this.getUserApp(user, userApp.domain);

    if (!!domainApp) {
      return true;
    }

    return false;
  }

  /**
   * Est-ce que le user est admin sur le scope en paramètre
   */
  // public isAdminOld(user: UserVO, userApp: UserApplicationVO): boolean {
  //   // sur domain, on a juste à regarder si on a le user_app du domain avec le role admin
  //   const domainApp: UserApplicationVO = this.getUserApp(user, userApp.domain);
  //   // si on est admin sur le domaine, on peut absolument tout voir
  //   if (!!domainApp && domainApp.isAdmin()) {
  //     return true;
  //   } else {
  //     const roles: RoleVO[] = this.resolveRolesForFeature(
  //       user, 
  //       StaticFeatureEnum.BU_ADMIN,
  //       userApp.domain,
  //       userApp.businessUnitCode
  //     );

  //     if (roles.length > 0) {
  //       return true;
  //     }

  //     // une vision.
  //     if (userApp.visionCode !== ViewData.wildcardDomain) {
  //       console.log('looking for vision', userApp.visionCode)
  //       const visionAdminRoles: RoleVO[] = this.resolveRolesForFeature(
  //         user, 
  //         StaticFeatureEnum.APP_ADMIN,
  //         ViewData.wildcardDomain, // userApp.domain,
  //         ViewData.wildcardDomain, //userApp.businessUnitCode,
  //         ViewData.wildcardDomain, //userApp.visionCode
  //       );

  //       return !!visionAdminRoles && visionAdminRoles.length > 0;
  //     }
  //   }
  // }

  /**
   * Retrouve les rôles pour la feature donnée avec le scope donné.
   *
   * La résolution se fait de manière ascendante.
   * C'est à dire que si on ne trouve pas le rôle dans le scope donné on vérifie pour le scope supérieur jusqu'à remonter jusqu'au scope domain
   *
   * @param domain Le code du domaine pour le scope
   * @param bu Le code de la bu pour le scope
   * @param visionCode Le code de la vison pour le scope
   * @param featureCode Le code de la feature à recherer
   * @param byPassCurrentLevel indique qu'on veut seulement checker les niveaux supérieur
   */
  public resolveRolesForFeature(user: UserVO, featureCode: string, domain: string, bu: string = '*', visionCode: string = '*', byPassCurrentLevel: boolean = false): RoleVO[] {
    let result: RoleVO[] = [];

    if (!byPassCurrentLevel) {
      const appConf: UserApplicationVO = this.getUserApp(user, domain, bu, visionCode, FEATURE_PERIMETER.APP_CONF);

      if (!!appConf) {
        result = result.concat(appConf.getFeatureRoles(featureCode));
      }

      const app: UserApplicationVO = this.getUserApp(user, domain, bu, visionCode, FEATURE_PERIMETER.APP);

      if (!!app) {
        result = result.concat(app.getFeatureRoles(featureCode));
      }
    }

    // on est sur le périmètre APP ou APP_CONF, on vérifie pour la BU parente
    if (bu !== ViewData.wildcardDomain && visionCode === ViewData.wildcardDomain) {
      const buapp = this.getUserApp(user, domain, bu);

      if (!!buapp) {
        result = result.concat(buapp.getFeatureRoles(featureCode));
      }
    }

    // résolution ascendante tant qu'on un parentCode pour la bu
    if (bu !== ViewData.wildcardDomain) {
      let buvo: BusinessUnitVO = this._model.businessUnitEngine.getByBusinessUnitCode(domain, bu);

      while (!!buvo && !!buvo.parentCode) {
        const parentApp = this.getUserApp(user, domain, buvo.parentCode);

        if (!!parentApp) {
          result = result.concat(parentApp.getFeatureRoles(featureCode));
        }

        buvo = this._model.businessUnitEngine.getByBusinessUnitCode(domain, buvo.parentCode);
      }
    }

    // on finit par le domaine
    const domainApp = this.getUserApp(user, domain);

    if (!!domainApp) {
      result = result.concat(domainApp.getFeatureRoles(featureCode));
    }

    return result;
  }



  /**
   * Vérifie si le user a le role pour le user app du contexte en cours
   * @param role Le role testé
   * @return true si le user a le role en paramètre
   */
  public hasRole(user: UserVO, role: RoleVO, perimeter: UserApplicationVO, checkProfiles: boolean = false): boolean {
    const visionApp: UserApplicationVO = this.getUserApp(
      user, 
      perimeter.domain,
      perimeter.businessUnitCode,
      perimeter.visionCode,
      perimeter.perimeter,
      false
    );

    return visionApp && visionApp.hasRole(role, checkProfiles);
  }

  /**
   * Vérifie si le user possède le role en paramètre
   * @param role Le profile recherché
   * @returns
   */
  public hasProfile(user: UserVO, profile: ProfileVO): boolean {
    // Un profile est donné créé sur un périmètre précis. Pas besoin d'aller voir dans les user app de plus haut niveau
    const app: UserApplicationVO = this.getUserApp(
      user, 
      profile.domain,
      profile.businessUnitCode,
      profile.visionCode,
      profile.perimeter,
      false
    );

    // on est sur le main on va pas voir plus haut
    if (!!app) {
      if (app.hasProfile(profile)) {
        return true;
      } else if (app.main) {
        return false;
      }
    }

    return false;
  }

  /**
   * Ajout du profile dans le user_app correspondant. Si le user_app n'existe pas, on le créé
   * @param profile le profile à ajouter
   */
  public addProfile(user: UserVO, profile: ProfileVO): UserApplicationVO {
    const app: UserApplicationVO = this.getUserApp(user, profile.domain, profile.businessUnitCode, profile.visionCode, profile.perimeter, true, true);

    if (!app.hasProfile(profile)) {
      app.addProfile(profile);
    }

    return app;
  }

  public removeProfile(user: UserVO, profile: ProfileVO): UserApplicationVO {
    let appIdx: number = -1;
    let profIdx: number = -1;

    let result: UserApplicationVO = null;

    user.userApplicationsVO.forEach((ua: UserApplicationVO, idApp: number) => { // on balaie les userApps...
      if(ua.domain === profile.domain && ua.businessUnitCode === profile.businessUnitCode ) {
        ua.profiles.forEach((p: Profile, idProfile: number) => {// si correspondance, on cherche le profile
          if(p.code === profile.code) {
            // console.log(' dead profile', p, idApp, idProfile);
            appIdx = idApp;
            profIdx = idProfile;
          }
        });
      }
    });
    
    if(profIdx > -1) { // on a trouvé le profile
      result = user.userApplicationsVO[appIdx];
      user.userApplicationsVO[appIdx].profiles.splice(profIdx, 1);
      if(user.userApplicationsVO[appIdx].profiles.length === 0 && user.userApplicationsVO[appIdx].roles.length === 0) { // le userApp est mort...
        user.userApplicationsVO.splice(appIdx, 1);
      }
    }

    return result;
  }

 /**
   * merge d'un user
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: UserVO, from: UserVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  /**
   * delete d'un profil
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  // tslint:disable-next-line: ban-types
  public remove(to: UserVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  protected backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: UserVO = cmd.sourceVO[0] as UserVO;
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone

            // console.log('user commit return', source);

            const modelUser: UserVO = this._model.usersMap.get(source.mail);
            if (!!modelUser) {
              modelUser.source = source;

              /**
               * C'est le user connecté qui est mis à jour on sync le model
               */
              if (source.mail === this.model.connectedUser.mail) {
                this.model.connectedUser.source = source;
              }
            }
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            this._model.usersMap.set(vo.mail, vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = source;
            this._model.usersMap.delete(vo.mail);
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }

  /**
   * Est-ce que le user est admin sur le scope en paramètre
   * dom
   * bu
   * vision
   * Prise en compte d'éventuels héritages
   * @param user concerné
   * @param userApp cible
   * @return true si admin
   * @return true si l'admin vient de plus haut
   */
  public isAdmin(user: UserVO, userApp: UserApplicationVO, verbose: boolean = false): [boolean, boolean] {
    
    if(!user) {
      return [false, false];
    }

    let isAdmin: boolean = false;
    let isHigher: boolean = false;
    let appPerimeter: string  = userApp.perimeter; // le périmètre de recherche pour "Admin"
    let adminTarget: string  = null; // celui qui répond OK pour "Admin"
    let roles: RoleVO[];

    // Regarder si on a le user_app du domain avec le role admin
    // const domainApp: UserApplicationVO = userApp.perimeter === FEATURE_PERIMETER.DOM ? userApp : null; // this.getUserApp(user, userApp.domain);
    const domainApp: UserApplicationVO = this.getUserApp(user, userApp.domain);
    // si on est admin sur le domaine, on peut absolument tout voir

    if (!!domainApp && domainApp.isAdmin()) {

      isAdmin = true;
      adminTarget = FEATURE_PERIMETER.DOM;
      if(appPerimeter !== FEATURE_PERIMETER.DOM) {
        isHigher = true;
      }
    } 
    
    if(!isAdmin && appPerimeter !== FEATURE_PERIMETER.DOM) { // pas OK sur DOMAIN, on va chercher sur BU
      
      userApp.rolesVO.forEach(rvo => {
        if(rvo.code === StaticFeatureEnum.BU_ADMIN) {
          isAdmin = true;
          adminTarget = 'BU';
          // console.log('self admin', user?.mail, userApp.code, userApp.businessKey);
        }
      });
      
      if(!isAdmin) { // pas directement sur la BU
        roles = this.resolveRolesForFeature(
          user, 
          StaticFeatureEnum.BU_ADMIN, // on cherche Admin de la BU
          userApp.domain,
          userApp.businessUnitCode
        );

        if (roles.length > 0) { // un rôle buAdmin sur la BU > 1 sinon c'estpas moi (juste avant) donc c'est papa ou pépé
          isAdmin = true;
          adminTarget = 'other BU';
          isHigher = true;
          if (appPerimeter === FEATURE_PERIMETER.BU) {
            isHigher = true;
              // console.log('higher other BU', userApp.businessUnitCode);
          } else if (appPerimeter === FEATURE_PERIMETER.APP_CONF) {
            isHigher = true;
            // console.log('higher admin on BU', userApp.businessUnitCode);
          }
          // roles.forEach(r => {
          //   if(r.businessUnitCode !== userApp.businessUnitCode) { // un rôle parent à répondu
          //     isHigher = true;
          //     console.log('higher other BU', userApp.businessUnitCode, r.businessUnitCode)
          //   }
          // });
        }
      }
    }
    
    if(!isAdmin && appPerimeter === FEATURE_PERIMETER.APP_CONF) { // pas OK sur BU, on va chercher sur vision
      
      let visionResult: [boolean, boolean] = this.isVisionAdmin(user, userApp, verbose);
      isAdmin = visionResult[0];
      isHigher = visionResult[1];
    }

    // if(result && user.mail === 'a@a.net') {
    //   //console.log('vision admin', userApp.businessKey, fromParent, adminTarget);
    // }
    if(verbose) {
      // console.log('Admin', user.mail, userApp.businessKey, isAdmin, isHigher, adminTarget);
    }

    return [isAdmin, isHigher];
  }

  /**
   * Est-ce que le user est admin sur la vision ? directement ou par héritage
   * @param user concerné
   * @param userApp cible
   * @return true si admin
   * @return true si l'admin vient de plus haut
   */
  public isVisionAdmin(user: UserVO, userApp: UserApplicationVO, verbose: boolean = false): [boolean, boolean] {
    
    if(!user) {
      return [false, false];
    }

    let isAdmin: boolean = false;
    let isHigher: boolean = false;
    let adminTarget: string  = null; // celui qui répond OK pour "Admin"
    let roles: RoleVO[];

    // Regarder si on a le user_app du domain avec le role admin
    // const domainApp: UserApplicationVO = userApp.perimeter === FEATURE_PERIMETER.DOM ? userApp : null; // this.getUserApp(user, userApp.domain);
    const domainApp: UserApplicationVO = this.getUserApp(user, userApp.domain);
    // si on est admin sur le domaine, on peut absolument tout voir

      
    // on cherche un "Admin de toutes les visions du DOMAINE"
    if (!!domainApp) {
      domainApp.rolesVO.forEach(rvo => {
        if(rvo.code === StaticFeatureEnum.DOM_VISION_ADMIN) {
          // console.log('dom vision admin');
          isAdmin = true;
          adminTarget = 'Visions of DOM';
          isHigher = userApp.perimeter !== FEATURE_PERIMETER.DOM;
        }
      });
    }

    if(!isAdmin) { // on cherche le role directement sur la BU
      if(userApp.perimeter === FEATURE_PERIMETER.BU) {
        userApp.rolesVO.forEach(rvo => {
          if(rvo.code === StaticFeatureEnum.BU_VISION_ADMIN) {
            // console.log('dom vision admin');
            isAdmin = true;
            adminTarget = 'Visions of BU';
            isHigher = false;
          }
        });
      }
    }

    // on cherche un "Admin de toutes les visions des BUs mères"
    // TODO TODO ici, héritage de BU pour isHigher
    if(!isAdmin) {
      roles = this.resolveRolesForFeature(
        user, 
        StaticFeatureEnum.BU_VISION_ADMIN,
        userApp.domain,
        userApp.businessUnitCode
      );
      if (roles.length > 0) { // un rôle visionAdmin sur la BU
        isAdmin = true;
        adminTarget = 'Visions of BUs';
        isHigher = true;
      }
    };
      
      
    // sinon le rôle admin directement sur le userApp
    if(!isAdmin) {
      userApp.rolesVO.forEach(rvo => {
        if(rvo.code === FEATURE_PERIMETER.APP_CONF) {
          isAdmin = true;
          adminTarget = 'Vision';
        }
      });
      
    }

    // if(result && user.mail === 'a@a.net') {
    //   //console.log('vision admin', userApp.businessKey, fromParent, adminTarget);
    // }
    if(verbose) {
      // console.log('Admin vision', user.mail, userApp.businessKey, isAdmin, isHigher, adminTarget);
    }

    return [isAdmin, isHigher];
  }


}
